import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErr, fmtDate } from "@/merenda/common";

const AUTO_REASON = "Completado a partir do 1º dia (variação automática)";
const vary = (v) => { const lo = Math.ceil(v * 0.9); return lo + Math.floor(Math.random() * (v - lo + 1)); };
const ck = (g, m) => `${g}|${m}`;

const BaseGrid = ({ meta, base, setBase }) => (
  <div className="overflow-x-auto rounded border">
    <table className="w-full text-sm">
      <thead><tr className="bg-slate-50 text-xs text-slate-500"><th className="p-2 text-left">Turno</th>{meta.modalities.map(([k, l]) => <th key={k} className="p-2 text-right">{l}</th>)}</tr></thead>
      <tbody>{meta.groups.map(([g, gl]) => (
        <tr key={g} className="border-t"><td className="p-2 font-medium">{gl}</td>
          {meta.modalities.map(([m]) => <td key={m} className="p-1"><Input type="number" min={0} className="ml-auto h-8 w-20" value={base[ck(g, m)] ?? ""} onChange={(e) => setBase({ ...base, [ck(g, m)]: e.target.value })} data-testid={`first-count-${g}-${m}`} /></td>)}
        </tr>))}</tbody>
    </table>
  </div>
);

const ReviewGrid = ({ cols, colLb, rows, setRows }) => (
  <div className="max-h-[45vh] overflow-auto rounded border">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-slate-50"><tr className="text-xs text-slate-500"><th className="p-2 text-left">Dia</th>{cols.map((c) => <th key={c} className="p-2 text-right">{colLb(c)}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={r.date} className="border-t" data-testid={`complete-row-${r.date}`}><td className="whitespace-nowrap p-2">{fmtDate(r.date)}</td>
          {cols.map((c) => <td key={c} className="p-1"><Input type="number" min={0} className="ml-auto h-8 w-20" value={r.v[c]} onChange={(e) => setRows(rows.map((y, j) => (j === i ? { ...y, v: { ...y.v, [c]: e.target.value } } : y)))} data-testid={`complete-count-${r.date}-${c.replace("|", "-")}`} /></td>)}
        </tr>))}</tbody>
    </table>
  </div>
);

export const FirstDayDialog = ({ date, meta, scopeParams, pendingDays, onClose, onDone }) => {
  const [base, setBase] = useState({});
  const [reason, setReason] = useState(AUTO_REASON);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const gl = Object.fromEntries(meta.groups), ml = Object.fromEntries(meta.modalities);
  const cols = Object.keys(base).filter((c) => Number(base[c]) > 0);
  const colLb = (c) => { const [g, m] = c.split("|"); return `${gl[g]} · ${ml[m]}`; };
  const complete = () => setRows(pendingDays.filter((d) => d > date).map((d) => ({ date: d, v: Object.fromEntries(cols.map((c) => [c, vary(Number(base[c]))])) })));
  const send = async (withRest) => {
    const all = [{ date, v: Object.fromEntries(cols.map((c) => [c, base[c]])) }, ...(withRest ? rows : [])];
    if (all.some((r) => cols.some((c) => r.v[c] === "" || Number(r.v[c]) < 0))) { toast.error("Preencha todos os valores"); return; }
    const items = all.flatMap((r, i) => cols.map((c) => { const [group, modality] = c.split("|"); return { date: r.date, group, modality, count: Number(r.v[c]), auto: i > 0 }; }));
    setBusy(true);
    try { await api.post("/merenda/attendance/bulk", { reason, items }, { params: scopeParams }); toast.success(`Presença gravada em ${all.length} dia(s)`); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="presence-dialog">
        <DialogHeader><DialogTitle>Conferir presença — {fmtDate(date)} (1º dia)</DialogTitle></DialogHeader>
        {rows ? <p className="text-xs text-slate-500" data-testid="complete-info">{rows.length} dias pendentes preenchidos com variação de até -10% do 1º dia (nunca acima). Revise antes de confirmar.</p>
          : <p className="text-xs text-slate-500">Informe os alunos presentes em cada turno e modalidade. Só os campos maiores que zero são gravados e completados.</p>}
        {rows ? <ReviewGrid cols={cols} colLb={colLb} rows={rows} setRows={setRows} /> : <BaseGrid meta={meta} base={base} setBase={setBase} />}
        <div><Label>Fonte / justificativa</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="presence-reason" /></div>
        <DialogFooter className="gap-2">
          {rows ? <>
            <Button variant="outline" onClick={() => setRows(null)} data-testid="complete-back">Voltar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy || reason.trim().length < 3} onClick={() => send(true)} data-testid="complete-confirm">Confirmar {rows.length + 1} dias</Button>
          </> : <>
            <Button variant="outline" className="border-orange-300 text-orange-700" disabled={!cols.length} onClick={complete} data-testid="presence-complete">Completar</Button>
            <Button disabled={busy || !cols.length || reason.trim().length < 3} onClick={() => send(false)} data-testid="presence-save">Salvar só o 1º dia</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
